import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Plus, PanelLeftClose, PanelLeft, ExternalLink } from 'lucide-react';
import './os.css';
import { WORKSPACES, WORKSPACE_BY_ID, MOBILE_NAV } from './osConfig';
import OSCommand from './OSCommand';
import OSQuickCreate from './OSQuickCreate';
import WorkspaceView from './WorkspaceView';
import MyAceLive from './MyAceLive';
import ServicesLive from './ServicesLive';
import NetworkLive from './NetworkLive';
import BusinessLive from './BusinessLive';
import AutoLifestyleLive from './AutoLifestyleLive';
import ClientsDirectory from './ClientsDirectory';

// ACE OS 2.0 responsive shell (UI-R1). Parallel to /command-center; migrates workspaces later.
export default function OSShell() {
  const { workspace: wsParam } = useParams();
  const navigate = useNavigate();
  const wsId = WORKSPACE_BY_ID[wsParam] ? wsParam : 'my-ace';
  const ws = WORKSPACE_BY_ID[wsId];

  const [section, setSection] = useState(ws.sections[0].id);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => { setSection(ws.sections[0].id); }, [wsId]); // reset section on workspace change

  const goWs = useCallback((id) => { setMoreOpen(false); navigate(`/os/${id}`); }, [navigate]);

  // Cmd/Ctrl+K opens command
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdOpen(true); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const mobileActiveId = MOBILE_NAV.some((m) => m.id === wsId) ? wsId : 'more';

  return (
    <div className="os-root" data-testid="os-shell">
      <div className="flex min-h-screen">
        {/* ===== Desktop / tablet sidebar ===== */}
        <aside className={`hidden md:flex flex-col shrink-0 border-r os-surface transition-all duration-200 ${railCollapsed ? 'w-[76px]' : 'w-[76px] lg:w-60'}`}
               style={{ borderColor: 'var(--os-border)' }} data-testid="os-sidebar">
          <div className="flex items-center gap-2 px-4 h-16 border-b" style={{ borderColor: 'var(--os-border)' }}>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center font-black text-white shrink-0" style={{ background: 'var(--os-red)' }}>A</div>
            {!railCollapsed && <div className="leading-none hidden lg:block"><p className="os-display text-lg">ACE OS</p><p className="text-[10px]" style={{ color: 'var(--os-muted)' }}>2.0 · Auto Lifestyle</p></div>}
          </div>
          <nav className="flex-1 p-2 space-y-1 os-scroll overflow-y-auto">
            {WORKSPACES.map((w) => {
              const Icon = w.icon;
              return (
                <button key={w.id} onClick={() => goWs(w.id)} data-testid={`os-nav-${w.id}`}
                        className={`os-nav-item ${w.id === wsId ? 'active' : ''}`} title={w.title}>
                  <Icon className="h-5 w-5 shrink-0" />
                  {!railCollapsed && <span className="truncate hidden lg:inline">{w.title}</span>}
                </button>
              );
            })}
          </nav>
          <div className="p-2 border-t" style={{ borderColor: 'var(--os-border)' }}>
            <button onClick={() => setRailCollapsed((v) => !v)} className="os-nav-item hidden lg:flex" data-testid="os-collapse">
              {railCollapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
              {!railCollapsed && <span className="hidden lg:inline">Collapse</span>}
            </button>
          </div>
        </aside>

        {/* ===== Main column ===== */}
        <div className="flex-1 min-w-0 flex flex-col pb-[76px] md:pb-0">
          {/* Top command bar */}
          <header className="sticky top-0 z-40 os-surface border-b" style={{ borderColor: 'var(--os-border)' }} data-testid="os-topbar">
            <div className="h-16 px-4 flex items-center gap-3">
              <button onClick={() => setCmdOpen(true)} data-testid="os-search-trigger"
                      className="flex-1 max-w-xl flex items-center gap-2 os-input" style={{ minHeight: 42 }}>
                <Search className="h-4 w-4" style={{ color: 'var(--os-muted)' }} />
                <span className="text-sm" style={{ color: 'var(--os-muted)' }}>Search everything…</span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded" style={{ color: 'var(--os-muted)', border: '1px solid var(--os-border)' }}>⌘K</span>
              </button>
              <button onClick={() => setQcOpen(true)} className="os-btn-primary" data-testid="os-quick-create-trigger">
                <Plus className="h-4 w-4" /><span className="hidden sm:inline">Create</span>
              </button>
            </div>
          </header>

          {/* Workspace header + contextual section tabs */}
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: 'var(--os-red)' }}>Workspace</p>
            <h1 className="os-display text-4xl sm:text-5xl mt-1" data-testid="os-workspace-title">{ws.title}</h1>
            <p className="text-sm mt-1 max-w-2xl" style={{ color: 'var(--os-muted)' }}>{ws.blurb}</p>
            <div className="flex gap-2 mt-4 overflow-x-auto os-scroll pb-1" data-testid="os-section-tabs">
              {wsId !== 'my-ace' && wsId !== 'services' && wsId !== 'network' && wsId !== 'auto-lifestyle' && wsId !== 'clients' && wsId !== 'business' && ws.sections.map((s) => (
                <button key={s.id} onClick={() => setSection(s.id)} data-testid={`os-sectiontab-${s.id}`}
                        className={`os-section-tab ${s.id === section ? 'active' : ''}`}>{s.label}</button>
              ))}
            </div>
          </div>

          {/* Body */}
          <main className="px-4 sm:px-6 py-5 flex-1">
            {wsId === 'my-ace' ? <MyAceLive />
              : wsId === 'services' ? <ServicesLive />
              : wsId === 'network' ? <NetworkLive />
              : wsId === 'auto-lifestyle' ? <AutoLifestyleLive />
              : wsId === 'clients' ? <ClientsDirectory />
              : wsId === 'business' ? <BusinessLive />
              : <WorkspaceView workspace={ws} activeSection={section} />}
          </main>
        </div>
      </div>

      {/* ===== Mobile bottom nav ===== */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 os-surface border-t flex"
           style={{ borderColor: 'var(--os-border)', height: 68, paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="os-bottom-nav">
        {MOBILE_NAV.map((m) => {
          const Icon = m.icon;
          const active = m.id === mobileActiveId;
          const onClick = () => (m.id === 'more' ? setMoreOpen(true) : goWs(m.id));
          return (
            <button key={m.id} onClick={onClick} data-testid={`os-bnav-${m.id}`}
                    className="flex-1 flex flex-col items-center justify-center gap-0.5"
                    style={{ color: active ? 'var(--os-red)' : 'var(--os-muted)' }}>
              <Icon className="h-5 w-5" />
              <span className="text-[10px] font-bold">{m.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Mobile floating + */}
      <button onClick={() => setQcOpen(true)} data-testid="os-fab"
              className="md:hidden fixed right-4 z-50 h-14 w-14 rounded-full flex items-center justify-center shadow-lg"
              style={{ background: 'var(--os-red)', bottom: 84 }}>
        <Plus className="h-6 w-6 text-white" />
      </button>

      {/* Mobile "More" sheet: Network + Business + legacy */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[110] flex items-end" style={{ background: 'rgba(0,0,0,.72)' }} onClick={() => setMoreOpen(false)} data-testid="os-more-sheet">
          <div className="os-elev w-full rounded-t-2xl p-4 os-animate" onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}>
            <p className="os-display text-xl mb-3">MORE</p>
            {WORKSPACES.filter((w) => !MOBILE_NAV.some((m) => m.id === w.id)).map((w) => {
              const Icon = w.icon;
              return (
                <button key={w.id} onClick={() => goWs(w.id)} className="os-nav-item" data-testid={`os-more-${w.id}`}>
                  <Icon className="h-5 w-5" /><span>{w.title}</span>
                </button>
              );
            })}
            <button onClick={() => { setMoreOpen(false); navigate('/command-center'); }} className="os-nav-item" data-testid="os-more-legacy">
              <ExternalLink className="h-5 w-5" /><span>Advanced · Legacy Command Center</span>
            </button>
          </div>
        </div>
      )}

      <OSCommand open={cmdOpen} onClose={() => setCmdOpen(false)} />
      <OSQuickCreate open={qcOpen} onClose={() => setQcOpen(false)} />
    </div>
  );
}
